import { play } from 'cuelume'

import type { SubmitterContext } from '@/features/order/order-form/lib/submitters/types'
import { track } from '@/lib/analytics'
import { tradeVenueName } from '@/lib/analytics-format'
import { toast } from '@/lib/toast'
import { randomUUID } from '@/lib/utils'
import type { useStartSingleHedgeAlgo } from '@/service/algo.hook'
import type { SingleHedgeAlgoParams } from '@/types/generated/algo'
import type { PublicAccount } from '@/types/generated/common/account'
import type { Exchange } from '@/types/generated/common/types'
import type { ErrorResponse } from '@/types/service'

/** The account the Hedge tab hedges and the ticket's selected instrument ('' for none). */
export type SingleHedgeTarget = {
  account: PublicAccount
  exchange: Exchange
  selectedSymbol: string
}

/** The Hedge tab's validated hedge and the account it runs under. */
export type SingleHedgeSubmission = SingleHedgeTarget & { params: SingleHedgeAlgoParams }

export type SubmitSingleHedgeDeps = {
  // Toasts and returns null when the tab can't be submitted yet.
  buildSingleHedge: () => SingleHedgeSubmission | null
  startSingleHedge: ReturnType<typeof useStartSingleHedgeAlgo>['mutateAsync']
}

export async function submitSingleHedgeOrder(ctx: SubmitterContext, deps: SubmitSingleHedgeDeps) {
  const hedge = deps.buildSingleHedge()
  if (!hedge) return

  ctx.setIsSubmitting(true)
  try {
    await deps.startSingleHedge({ ...hedge.params, correlationId: randomUUID() })
    ctx.markOrderExecuted()
    void track('Start Delta Hedge', {
      exchange: tradeVenueName(hedge.account.tradeVenue),
      instrument: hedge.params.hedgeInstrument ?? '',
    })
    // No success toast — the WS algoLifecycle STARTED notification already shows one. The
    // rows are left alone: the hedge never read their sizes or prices.
  } catch (error) {
    toast.error((error as ErrorResponse)?.message || ctx.t('hedgeFailed'), {
      position: 'bottom-right',
    })
    play('error')
  } finally {
    ctx.setIsSubmitting(false)
  }
}
